/**
 * ChequeSettingsList - Displays list of cheque settings with management actions
 */

import type { ChequeSettingsPublic } from '@garden-platform/shared';
import type * as React from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useChequeSettings, useDeleteChequeSettings } from '../../hooks';
import { ChequeSettingsCard } from './ChequeSettingsCard';
import { ChequeSettingsFormDialog } from './ChequeSettingsFormDialog';

export interface ChequeSettingsListProps {
  className?: string;
}

export const ChequeSettingsList: React.FC<ChequeSettingsListProps> = ({ className }) => {
  const [showInactive, setShowInactive] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSettings, setEditingSettings] = useState<ChequeSettingsPublic | undefined>();

  const { data: chequeSettings, isLoading } = useChequeSettings(showInactive);
  const deleteMutation = useDeleteChequeSettings();

  const handleEdit = (settings: ChequeSettingsPublic) => {
    setEditingSettings(settings);
    setIsDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingSettings(undefined);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this cheque configuration?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleDialogClose = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      setEditingSettings(undefined);
    }
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">Loading cheque settings...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Cheque Settings</CardTitle>
              <CardDescription>
                Configure cheque printing options for each bank account
              </CardDescription>
            </div>
            <Button onClick={handleAdd}>Add Configuration</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="showInactiveChequeSettings"
              checked={showInactive}
              onCheckedChange={(checked) => setShowInactive(checked === true)}
            />
            <Label htmlFor="showInactiveChequeSettings" className="text-sm font-normal">
              Show inactive configurations
            </Label>
          </div>

          {!chequeSettings || chequeSettings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-muted-foreground mb-4">No cheque configurations found</p>
              <Button variant="outline" onClick={handleAdd}>
                Create your first configuration
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {chequeSettings.map((settings) => (
                <ChequeSettingsCard
                  key={settings.id}
                  chequeSettings={settings}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isDeleting={deleteMutation.isPending}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ChequeSettingsFormDialog
        open={isDialogOpen}
        onOpenChange={handleDialogClose}
        chequeSettings={editingSettings}
      />
    </>
  );
};

ChequeSettingsList.displayName = 'ChequeSettingsList';
